import React from 'react';
import { useFlavour } from '../../context/FlavourContext';
import { LIFESTYLE } from '../../data/mock';
import Reveal from '../../components/Reveal';

export function Lifestyle() {
  const { flavour } = useFlavour();
  return (
    <section className="relative">
      {/* main cinematic parallax */}
      <div className="relative h-[80vh] md:h-screen overflow-hidden flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center md:bg-fixed"
          style={{ backgroundImage: `url(${LIFESTYLE.cinemaMain})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-[#050505]/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/20 to-transparent" />
        <div className="flavour-transition absolute inset-0" style={{ background: `radial-gradient(ellipse 50% 60% at 15% 60%, rgba(${flavour.rgb},0.20), transparent 70%)` }} />
        <div className="relative max-w-[1400px] mx-auto px-5 w-full">
          <Reveal><p className="font-display text-4xl md:text-8xl text-white/90 leading-[0.9]">IT'S NOT<br />WHAT YOU SAY...</p></Reveal>
          <Reveal delay={150}><p className="font-display text-4xl md:text-8xl mt-3 leading-[0.9]">IT'S WHAT<br /><span className="text-flavour flavour-transition flavour-text-glow">YOU SHOW.</span></p></Reveal>
          <Reveal delay={300}><p className="font-cond tracking-[0.25em] text-white/70 mt-8 text-lg md:text-xl">CONFIDENCE ISN'T ANNOUNCED. IT'S FELT.</p></Reveal>
        </div>
      </div>

      {/* triptych band */}
      <div className="grid md:grid-cols-2">
        {[{ img: LIFESTYLE.cinemaRed, t: 'OWN THE', h: 'MOMENT.', s: 'When the lights go down, confidence speaks.' },
          { img: LIFESTYLE.cinemaFire, t: 'FEEL THE', h: 'HEAT.', s: 'Energy, attraction and presence \u2014 every night.' }].map((c, i) => (
          <div key={i} className="group relative h-[60vh] md:h-[80vh] overflow-hidden">
            <img src={c.img} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
            <div className="flavour-transition absolute inset-0 opacity-60" style={{ background: `linear-gradient(180deg, transparent 40%, rgba(${flavour.rgb},0.12))` }} />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
              <Reveal>
                <p className="font-display text-3xl md:text-5xl text-white/90 leading-none">{c.t}<br /><span className="text-flavour flavour-transition">{c.h}</span></p>
                <p className="text-white/60 mt-3 max-w-sm">{c.s}</p>
              </Reveal>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function HowToUse() {
  const { flavour } = useFlavour();
  const steps = [
    { n: '01', t: 'CHEW', d: 'One daily serving, wherever you are.' },
    { n: '02', t: 'GO', d: 'Fast-acting and easy to fit in your day.' },
    { n: '03', t: 'OWN THE MOMENT', d: 'Show up with energy and confidence.' },
  ];
  return (
    <section className="py-20 md:py-28">
      <div className="max-w-[1400px] mx-auto px-5">
        <Reveal className="text-center mb-14"><h2 className="font-display text-4xl md:text-6xl">READY WHEN <span className="text-flavour flavour-transition">YOU ARE.</span></h2></Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <Reveal key={s.n} delay={i * 120} className="relative rounded-2xl border border-white/10 bg-[#0b0b0b] p-8 overflow-hidden">
              <span className="font-display text-7xl text-white/5 absolute -top-2 right-4">{s.n}</span>
              <p className="font-display text-2xl" style={{ color: flavour.primary }}>{s.n}</p>
              <h3 className="font-display text-3xl mt-2">{s.t}</h3>
              <p className="text-white/60 mt-3">{s.d}</p>
            </Reveal>
          ))}
        </div>
        <p className="text-center text-xs text-white/30 mt-8 max-w-2xl mx-auto">Editable area: insert the exact legally approved usage instructions from your product label here.</p>
      </div>
    </section>
  );
}

export function Personalities() {
  const { order, all, setFlavour, flavourId } = useFlavour();
  return (
    <section className="py-20 md:py-28 bg-[#080808]">
      <div className="max-w-[1400px] mx-auto px-5">
        <Reveal className="text-center mb-12"><h2 className="font-display text-4xl md:text-6xl">FIND YOUR <span className="text-flavour flavour-transition">FLAVOUR.</span></h2></Reveal>
        <div className="flex gap-5 overflow-x-auto snap-x-carousel pb-4 md:grid md:grid-cols-3 lg:grid-cols-6 md:overflow-visible">
          {order.map((id, i) => {
            const f = all[id];
            const on = flavourId === id;
            return (
              <Reveal key={id} delay={i * 80} className="snap-item shrink-0 w-[80%] sm:w-[46%] md:w-auto">
                <button
                  onClick={() => setFlavour(id, 'personalities')}
                  className="relative w-full rounded-2xl overflow-hidden border p-6 text-left h-full transition-all duration-300"
                  style={{ borderColor: on ? f.primary : 'rgba(255,255,255,0.1)', background: `radial-gradient(circle at 50% 0%, rgba(${f.rgb},${on ? 0.22 : 0.08}), transparent 70%), #0b0b0b`, boxShadow: on ? `0 0 40px -12px rgba(${f.rgb},0.6)` : 'none' }}
                >
                  <img src={f.fruit} alt={f.name} loading="lazy" className="w-16 h-16 rounded-full object-cover border-2 mb-4" style={{ borderColor: f.primary }} />
                  <h3 className="font-display text-2xl" style={{ color: f.primary }}>{f.name}</h3>
                  <p className="font-cond tracking-wide text-white/70 text-sm mt-1">{f.tagline}</p>
                  <p className="text-white/50 text-sm mt-3">{f.personality}</p>
                </button>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
